// Builds color mapping data and ordering for visualization headers based on layout and items.
import type { SwimlaneLayoutDTO, TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { useMemo } from 'react';
import { useItemsWithParentsForHeaders } from '@/components/loop-visualizations/layout/colors-panel/hooks/useItemsWithParentsForHeaders';
import { useOrderedHeaderValues } from '@/components/loop-visualizations/layout/colors-panel/hooks/useOrderedHeaderValues';
import { useUniqueHeaderValues } from '@/components/loop-visualizations/layout/colors-panel/hooks/useUniqueHeaderValues';
import { isHeaderDate } from '@/components/loop-visualizations/layout/header-menu/utils';
import { filterItemsByLevel } from '@/components/views/utils/filterItemsByLevel';
import { useVisualizationHeaderOptions } from '@/components/views/utils/useVisualizationHeaderOptions';
import { getHeaderFieldId } from '@/components/views/utils/visualizationHeaderUtils';
import type { Item } from '@/lib/api/types/items';
import type {
  IPropertySelectorByID,
  ISwimlaneHeader,
  ItemStatusOption,
  ItemTypeLevelOption,
  ItemTypeOption,
} from '@/lib/api/types/roadmapping';
import { getPaletteSwatches } from '@/lib/utils/roadmapping/colors';
import { headerToPropertySelector } from '@/lib/utils/roadmapping/commonLayoutUtils';
import { useVisualizationColorItems } from './useVisualizationColorItems';
import { useVisualizationLabelMap } from './useVisualizationLabelMap';

type VisualizationLayout = SwimlaneLayoutDTO | TimelineLayoutDTO | null | undefined;

interface VisualizationHeaders {
  header1: IPropertySelectorByID | null;
  header2: IPropertySelectorByID | null;
  groupRowsByHeader?: IPropertySelectorByID | null;
  itemLevelId: number;
}

interface UseVisualizationColorDataParams {
  layout: VisualizationLayout;
  headers: VisualizationHeaders | undefined;
  items: Item[];
  itemTypes: ItemTypeOption[];
  itemLevels: ItemTypeLevelOption[];
  itemStatuses: ItemStatusOption[];
  noValueLabel: string;
  columnHeaderOrder?: string[];
  rowHeaderOrder?: string[];
  enabled?: boolean;
}

export function useVisualizationColorData({
  layout,
  headers,
  items,
  itemTypes,
  itemLevels,
  itemStatuses,
  noValueLabel,
  columnHeaderOrder,
  rowHeaderOrder,
  enabled = true,
}: UseVisualizationColorDataParams) {
  const colorConfig = layout?.colorConfig;

  const filteredItems = useMemo(() => {
    return filterItemsByLevel({ items, itemTypes, itemLevels, layout });
  }, [items, itemTypes, itemLevels, layout]);

  const allItemsWithParents = useItemsWithParentsForHeaders({
    filteredItems,
    allItems: items,
    enabled,
  });

  const itemLevelId = headers?.itemLevelId ?? 0;

  const statusOverride = enabled && layout?.showEmptyHeaders ? undefined : itemStatuses;

  const visualizationHeader1: ISwimlaneHeader | null = useVisualizationHeaderOptions(
    headers?.header1 ?? null,
    allItemsWithParents,
    itemLevelId,
    {
      itemTypes,
      itemLevels,
      itemStatuses: statusOverride,
      enabled,
    },
  );

  const visualizationHeader2: ISwimlaneHeader | null = useVisualizationHeaderOptions(
    headers?.header2 ?? null,
    allItemsWithParents,
    itemLevelId,
    {
      itemTypes,
      itemLevels,
      itemStatuses: statusOverride,
      enabled,
    },
  );

  const visualizationGroupRowsByHeader: ISwimlaneHeader | null = useVisualizationHeaderOptions(
    headers?.groupRowsByHeader ?? null,
    allItemsWithParents,
    itemLevelId,
    {
      itemTypes,
      itemLevels,
      itemStatuses: statusOverride,
    },
  );

  const header1FieldId = useMemo(() => {
    return getHeaderFieldId(visualizationHeader1) || null;
  }, [visualizationHeader1]);

  const header2FieldId = useMemo(() => {
    return getHeaderFieldId(visualizationHeader2) || null;
  }, [visualizationHeader2]);

  const groupRowsByFieldId = useMemo(() => {
    return getHeaderFieldId(visualizationGroupRowsByHeader) || null;
  }, [visualizationGroupRowsByHeader]);

  const defaultColorByField = useMemo(() => {
    if (header1FieldId && !isHeaderDate(header1FieldId)) {
      return header1FieldId;
    }
    if (header2FieldId && !isHeaderDate(header2FieldId)) {
      return header2FieldId;
    }
    return 'status';
  }, [header1FieldId, header2FieldId]);

  const currentColorByField = colorConfig?.colorByField ?? defaultColorByField;
  const isBaseColorSelected = colorConfig?.mode === 'baseColor';

  const colorByHeaderSelector = useMemo<IPropertySelectorByID | null>(() => {
    if (isBaseColorSelected || !currentColorByField) {
      return null;
    }

    try {
      return headerToPropertySelector(currentColorByField);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: Intentional warning for unexpected color field parse failure
      console.warn(
        '[useVisualizationColorData] headerToPropertySelector failed for field:',
        currentColorByField,
        error,
      );
      return null;
    }
  }, [currentColorByField, isBaseColorSelected]);

  const colorByFieldHeader: ISwimlaneHeader | null = useVisualizationHeaderOptions(
    colorByHeaderSelector,
    allItemsWithParents,
    itemLevelId,
    {
      itemTypes,
      itemLevels,
      itemStatuses,
      enabled,
    },
  );

  const matchingHeader = useMemo(() => {
    if (header1FieldId === currentColorByField) return visualizationHeader1;
    if (header2FieldId === currentColorByField) return visualizationHeader2;
    if (groupRowsByFieldId === currentColorByField) return visualizationGroupRowsByHeader;
    return null;
  }, [
    header1FieldId,
    header2FieldId,
    groupRowsByFieldId,
    currentColorByField,
    visualizationHeader1,
    visualizationHeader2,
    visualizationGroupRowsByHeader,
  ]);

  const headerForColorField = matchingHeader ?? colorByFieldHeader;

  const preferItemOrderForHeaderValues =
    layout?.visualizationType === 'timeline' &&
    currentColorByField === header2FieldId &&
    !layout?.showEmptyHeaders;

  const uniqueHeaderValues = useUniqueHeaderValues(
    items,
    currentColorByField,
    isBaseColorSelected,
    matchingHeader,
    layout,
    preferItemOrderForHeaderValues,
  );

  const currentHeaderColorOrder = colorConfig?.headerColorOrder || [];

  const orderedHeaderValues = useOrderedHeaderValues(
    uniqueHeaderValues,
    currentHeaderColorOrder,
    currentColorByField,
    layout,
    header1FieldId,
    header2FieldId,
    columnHeaderOrder,
    rowHeaderOrder,
  );

  const paletteSwatches = useMemo(() => {
    return getPaletteSwatches(colorConfig);
  }, [colorConfig]);

  const labelMap = useVisualizationLabelMap({
    headerForColorField: headerForColorField?.type === 'DateSelector' ? null : headerForColorField,
    currentColorByField,
    uniqueValues: uniqueHeaderValues,
    itemsForIdLookup: allItemsWithParents,
    noValueLabel,
  });

  const colorItems = useVisualizationColorItems({
    orderedFieldValues: orderedHeaderValues,
    colorConfig,
    paletteSwatches,
    labelMap,
  });

  const isColorByFieldAHeader =
    Boolean(currentColorByField) &&
    (currentColorByField === header1FieldId ||
      currentColorByField === header2FieldId ||
      currentColorByField === groupRowsByFieldId);

  return {
    layout,
    colorConfig,
    items,
    allItemsWithParents,
    visualizationHeader1,
    visualizationHeader2,
    header1FieldId,
    header2FieldId,
    defaultColorByField,
    currentColorByField,
    isBaseColorSelected,
    isColorByFieldAHeader,
    matchingHeader,
    headerForColorField,
    uniqueHeaderValues,
    orderedHeaderValues,
    currentHeaderColorOrder,
    paletteSwatches,
    labelMap,
    colorItems,
  };
}
